import {
  alpha,
  Box,
  AspectRatio,
  Group,
  Stack,
  Flex,
  Space,
  lighten,
  Title,
} from "@mantine/core";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactPlayer from "react-player/lazy";
import { useIsLaptopOrBigger, useStyles } from "@/styles/useStyles";
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconClock,
  IconHourglassHigh,
  IconInfoCircle,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import {
  useDescriptorsForVideo,
  useIsLoggedIn,
  useIsVideoPlaying,
} from "@/store/selectors";
import { useStore } from "@/store";
import { Navigation } from "@/services/Navigation";
import { Analytics } from "@/services/Analytics";
import travoltaGif from "@/assets/memes/travolta-confused-meme.gif";
import { Image } from "@/components/ui/Image";
import { ActionIcon } from "@/components/ui/ActionIcon";
import { Text } from "@/components/ui/Text";
import { IQuote } from "@/types/ApiTypes";
import { RealtimeDatabase } from "@/services/RealtimeDatabase";
import {
  usePercentWatchedOfVideo,
  useSecondsWatchedOfVideo,
  useIsRecommendedAdvizer,
} from "@/store/selectors/userProfileSelectors";
import {
  useAssignmentIdForAnalytics,
  useShowAssignmentHeader,
} from "@/store/selectors/assignmentSelectors";
import { config } from "@/config";
import {
  useActivePlaylist,
  // useIsFirstVideoInPlaylist,
  usePlaylistUnderstandingBeforeStepConfig,
  // useUserHasBeforeResponseToActivePlaylistStep,
} from "@/store/selectors/playlistSelectors";
import { PlaylistScale } from "./playlists/PlaylistScale";
import { CopyLink } from "./playlists/CopyPlaylistLink";
import { useDeviceType } from "@/hooks";
import { VideoFeedbackButtons } from "./VideoFeedbackButtons";
import { VideoLoginPrompt } from "./VideoLoginPrompt";
import { UserProfileService } from "@/services/UserProfileService";
import { useIsPartnerApp } from "@/store/selectors/featureSelectors";

let lastVideoTimeForSeeking = 0;
const START_VIDEO_FOR_PROMPT = false;
const SHOW_ADMIN_COPY_VIMEO_URL = false;
export const AdvizerVideo = React.memo(
  (props: {
    advizerId?: string;
    videoId: string;
    title?: string | ReactNode;
    url: string;
    questionNumber?: number;
    isInBackground?: boolean;
    withControls?: boolean;
    onClickNext?: () => void;
    onClickPrevious?: () => void;
    video?: any;
    quote?: IQuote;
    light?: boolean;
    showDescriptors?: boolean;
    isPlaylistVideo?: boolean;
    onPercentProgress?: (percentProgress: number) => void;
    onProgressEvery5Seconds?: () => void;
    isAdvizerProfilePage?: boolean;
    showFirstResponseOverlay?: boolean;
    playOnMount?: boolean;
    hideDescriptors?: boolean;
    autoplay?: boolean;
    forceNoLight?: boolean;
  }) => {
    const {
      videoId,
      advizerId,
      title,
      url,
      isInBackground,
      withControls,
      onClickNext,
      onClickPrevious,
      video,
      quote,
      light = true,
      showDescriptors = false,
      onPercentProgress,
      onProgressEvery5Seconds,
      isPlaylistVideo,
      showFirstResponseOverlay = false,
      playOnMount = false,
      questionNumber,
      hideDescriptors = false,
      forceNoLight,
    } = props;
    const { theme } = useStyles();
    const showAssignmentHeader = useShowAssignmentHeader();
    const assignmentIdForAnalytics = useAssignmentIdForAnalytics();
    const assignmentId = assignmentIdForAnalytics;
    const isVideoPlaying = useIsVideoPlaying(videoId);
    const isPartnerApp = useIsPartnerApp();

    const [, setIsVideoReady] = useState(light);
    const isLaptopOrBigger = useIsLaptopOrBigger();
    const reactPlayerRef = useRef<ReactPlayer>(null);
    const descriptors = useDescriptorsForVideo(videoId);
    const setActiveVideoId = useStore((state) => state.videos.setActiveVideoId);
    const [isPlayHovered, setIsPlayHovered] = useState(false);
    const isLoggedIn = useIsLoggedIn();
    const isAssignmentVideo = !!showAssignmentHeader;
    const [durationInSeconds, setDurationInSeconds] = useState(0);
    const [secondsWatched, setSecondsWatched] = useState(0);
    const [userSecondsWatched, setUserSecondsWatched] = useState(0);
    const [previousPlayedSeconds, setPreviousPlayedSeconds] = useState(0);
    const lastReportedPercent = useRef(0);
    const lastReportedSecondsWatched = useRef(0);
    const [hasSeeked, setHasSeeked] = useState(false);
    const [playNow, setPlayNow] = useState(false);
    const incrementNumVideosPlayed = useStore(
      (state) => state.user.incrementNumVideosPlayed,
    );
    const [videoError] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1.25);
    const secondsWatchedOfVideo = useSecondsWatchedOfVideo({
      vimeoId: video?.vimeoId,
    });
    const percentWatchedOfVideo = usePercentWatchedOfVideo({
      vimeoId: video?.vimeoId,
    });
    const isRecommendedAdvizer = useIsRecommendedAdvizer({
      advizerId: advizerId || video?.advizerId || "",
    });
    const [didShareUrlFail, setDidShareUrlFail] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    // const isFirstVideoInPlaylist = useIsFirstVideoInPlaylist();
    const [didPlayerUrlFail, setDidPlayerUrlFail] = useState(false);
    // const isRestrictedApp = useIsRestrictedApp();
    const activePlaylist = useActivePlaylist();
    const understandingBeforeStepConfig =
      usePlaylistUnderstandingBeforeStepConfig();
    const { isMobileDevice } = useDeviceType();
    // const userHasBeforeResponseToActivePlaylistStep =
    //   useUserHasBeforeResponseToActivePlaylistStep();

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    const [iosMountedFullPlayer, setIosMountedFullPlayer] = useState(false);
    const [iosAttemptAutoplay, setIosAttemptAutoplay] = useState(false);
    const playerRef = useRef<ReactPlayer>(null);
    const [explicitShowPrompt, setExplicitShowPrompt] = useState(false);

    const vimeoCfg = useMemo(
      () => ({
        vimeo: {
          playerOptions: {
            muted: 1, // iOS-friendly autoplay
            playsinline: 1, // avoid forced fullscreen
            autopause: 0, // multiple players won’t pause each other
            dnt: 1, // avoid tracker/cookie issues
            // title: 0, byline: 0, portrait: 0, // optional
          },
        },
      }),
      [],
    );

    const userDidWatchVideo = useMemo(() => {
      return percentWatchedOfVideo > 90;
    }, [percentWatchedOfVideo]);

    const shareCopyUrl = useMemo(() => {
      if (!url) return url;
      if (url.includes("?")) {
        return url;
      }
      return `${url}?share=copy`;
    }, [url]);

    // Reset login prompt when user logs in
    useEffect(() => {
      if (isLoggedIn) {
        setShowLoginPrompt(false);
      }
    }, [isLoggedIn]);

    const playerUrl = useMemo(() => {
      return `https://player.vimeo.com/video/${video?.vimeoId}`;
    }, [video?.vimeoId]);

    const vimeoThumbnailUrl = useMemo(() => {
      if (!video?.vimeoId) return true;
      return `https://vumbnail.com/${video.vimeoId}.jpg`;
    }, [video?.vimeoId]);

    const preppedUrl = useMemo(() => {
      if (isMobileDevice) {
        return shareCopyUrl;
      }
      if (!didPlayerUrlFail) {
        return playerUrl;
      }

      if (!didShareUrlFail) {
        return shareCopyUrl;
      }

      return url;
    }, [
      didPlayerUrlFail,
      didShareUrlFail,
      isMobileDevice,
      playerUrl,
      shareCopyUrl,
      url,
    ]);

    // console.log("VIDEO", {
    //   questionNumber,
    //   url,
    //   playerUrl,
    //   shareCopyUrl,
    //   preppedUrl,
    // });

    const VideoWatchedStatus = useMemo(() => {
      if (userDidWatchVideo) {
        return (
          <Group gap={4} justify="flex-end" p="xs">
            <Flex
              bg="green"
              display="flex"
              align="center"
              py={6}
              px={8}
              style={{ borderRadius: 4 }}
            >
              <IconCheck size={16} color={theme.colors.text[0]} />
              <Text c="text.0" size="xs" ml={6} fw={900}>
                Watched
              </Text>
            </Flex>
          </Group>
        );
      }

      if (percentWatchedOfVideo > 0) {
        return (
          <Group gap={4} justify="flex-end" p="xs">
            <Flex
              bg="blue"
              display="flex"
              align="center"
              py={6}
              px={8}
              style={{ borderRadius: 4 }}
            >
              <IconHourglassHigh size={16} color={theme.colors.text[0]} />
              <Text c="text.0" size="xs" ml={6} fw={900}>
                Keep watching...
              </Text>
            </Flex>
          </Group>
        );
      }

      return null;
    }, [percentWatchedOfVideo, theme.colors.text, userDidWatchVideo]);

    const { videoTitleFontSize, videoTitleLineClamp } = useMemo(() => {
      const titleLength = String(title).length;

      if (titleLength > 50) {
        return {
          videoTitleFontSize: 16,
          lineClamp: 2,
        };
      }

      return {
        videoTitleFontSize: 20,
        videoTitleLineClamp: 1,
      };
    }, [title]);

    const resetVideo = useCallback(() => {
      setPreviousPlayedSeconds(0);
      setHasSeeked(false);
      setSecondsWatched(0);
      setUserSecondsWatched(0);
      setIsPlayHovered(false);
    }, []);

    const handlePlayVideo = useCallback(() => {
      setActiveVideoId(videoId);

      if (!isLoggedIn && !config.allowUnauthenticatedVideoPlayback) {
        setShowLoginPrompt(true);
        Analytics.click({
          action: "Play Video > Not logged in > Show overlay",
          context: isPlaylistVideo ? "playlist" : "video",
          isRecommendedAdvizer,
        });

        return;
      }

      setShowLoginPrompt(false);
    }, [
      isLoggedIn,
      setActiveVideoId,
      videoId,
      isPlaylistVideo,
      isRecommendedAdvizer,
    ]);

    const handleVideoReady = useCallback(() => {
      setIsVideoReady(true);

      if (secondsWatchedOfVideo > 0) {
        reactPlayerRef.current
          ?.getInternalPlayer()
          ?.setCurrentTime(secondsWatchedOfVideo);
      }

      // setIsVideoLoading(false);
    }, [secondsWatchedOfVideo]);

    const handleVideoProgress = useCallback(
      (data: {
        played: number;
        playedSeconds: number;
        loaded: number;
        loadedSeconds: number;
      }) => {
        if (!isVideoPlaying) {
          console.log("progress but not playing");
          return;
        }

        const roundedExactPercent = Math.floor(data.played * 100);
        const roundedPercent = Math.floor((data.played * 100) / 5) * 5;
        let localHasSeeked = hasSeeked;

        const currentPlayedSeconds = Math.floor(data.playedSeconds);
        const newUserSecondsWatched =
          currentPlayedSeconds > 0 ? userSecondsWatched : 0;

        if (Math.abs(currentPlayedSeconds - previousPlayedSeconds) > 2.5) {
          setHasSeeked(true);
          localHasSeeked = true;
          Analytics.seekVideo({
            video,
            videoSeekFromSeconds: previousPlayedSeconds,
            videoSeekToSeconds: data.playedSeconds,
            videoDurationSeconds: durationInSeconds,
            videoHasSeeked: hasSeeked,
            isPlaylistVideo,
            context: isPlaylistVideo ? "playlist" : undefined,
            isAssignmentVideo,
            isRecommendedAdvizer,
          });
          setPreviousPlayedSeconds(data.playedSeconds);
          return;
        }

        setPreviousPlayedSeconds(data.playedSeconds);

        if (roundedPercent > lastReportedPercent.current) {
          lastReportedPercent.current = roundedPercent;
          setSecondsWatched(Math.floor(data.playedSeconds));
          onPercentProgress?.(roundedPercent);
        }

        if (
          currentPlayedSeconds >= 5 &&
          currentPlayedSeconds - lastReportedSecondsWatched.current >= 5
        ) {
          onProgressEvery5Seconds?.();
          lastReportedSecondsWatched.current = currentPlayedSeconds;
          Analytics.videoProgressEvery5Seconds({
            video,
            userSecondsWatched: newUserSecondsWatched,
            videoDurationSeconds: durationInSeconds,
            videoWatchedSeconds: Math.floor(data.playedSeconds),
            videoWatchedPercent: roundedPercent,
            videoHasSeeked: localHasSeeked,
            isPlaylistVideo,
            isPlaylistVideoString: isPlaylistVideo ? "true" : "false",
            context: isPlaylistVideo ? "playlist" : undefined,
            isAssignmentVideo,
            isRecommendedAdvizer,
          });
        }

        RealtimeDatabase.updateUserVideoData({
          vimeoId: video.vimeoId,
          data: {
            secondsWatched: Math.floor(data.playedSeconds),
            percentWatched: roundedExactPercent,
          },
          secondsToAdd: 1,
        });

        setUserSecondsWatched((s) => s + 1);
        lastVideoTimeForSeeking = data.playedSeconds;
        setSecondsWatched((s) => s + 1);
      },
      [
        durationInSeconds,
        hasSeeked,
        isAssignmentVideo,
        isPlaylistVideo,
        isVideoPlaying,
        onPercentProgress,
        onProgressEvery5Seconds,
        previousPlayedSeconds,
        userSecondsWatched,
        video,
        isRecommendedAdvizer,
      ],
    );

    const VideoTime = useMemo(() => {
      if (!video.durationDisplay) return <Space h={8} />;
      return (
        <Group gap={4} justify="flex-end" p="xs">
          <Flex
            bg="primary.9"
            display="flex"
            align="center"
            py={6}
            px={8}
            style={{ borderRadius: 4 }}
          >
            <IconClock size={16} color={theme.colors.text[2]} />
            <Text c="white" size="sm" ml={6} fw={900}>
              {video.durationDisplay}
            </Text>
          </Flex>
        </Group>
      );
    }, [theme.colors.text, video.durationDisplay]);

    const FloatingDescriptors = useMemo(() => {
      if ((isPlayHovered || isVideoPlaying || !light) && !showDescriptors) {
        return null;
      }

      return (
        <Stack
          w="100%"
          h="100%"
          pos="absolute"
          top={0}
          bottom={0}
          right={0}
          gap={0}
          style={{ zIndex: 0 }}
          justify="flex-end"
          align="space-between"
        >
          {hideDescriptors ? null : (
            <>
              {descriptors?.map((descriptor: any) => {
                return (
                  <Group key={descriptor.id} justify="flex-end" pr="xs" py={4}>
                    <Text
                      key={descriptor.id}
                      ta="right"
                      m={0}
                      p={6}
                      pr={6}
                      size="sm"
                      lh={1.2}
                      fw={700}
                      c="text.0"
                      bg="background.9"
                      style={{
                        zIndex: 0,
                        textShadow: "0 0 10px rgba(0, 0, 0, 1)",
                        borderRadius: 4,
                      }}
                    >
                      {descriptor.text}
                    </Text>
                  </Group>
                );
              })}
            </>
          )}
          <Group justify="space-between">
            {light && VideoWatchedStatus ? (
              VideoWatchedStatus
            ) : (
              <Space h={44} />
            )}
            {light && !forceNoLight ? VideoTime : <Space h={44} />}
          </Group>
        </Stack>
      );
    }, [
      isPlayHovered,
      isVideoPlaying,
      light,
      showDescriptors,
      hideDescriptors,
      descriptors,
      VideoWatchedStatus,
      forceNoLight,
      VideoTime,
    ]);

    const LoginOverlay = useMemo(() => {
      if (isLoggedIn || config.allowUnauthenticatedVideoPlayback) return null;

      // For playlists, show immediately if we're at the understanding step
      const shouldShowForPlaylist = isPlaylistVideo && showFirstResponseOverlay;

      // For regular videos, show when user tries to play
      const shouldShowForVideo = !isPlaylistVideo && showLoginPrompt;

      if (!shouldShowForPlaylist && !shouldShowForVideo) return null;

      return <VideoLoginPrompt isPlaylistVideo={isPlaylistVideo} />;
    }, [
      isLoggedIn,
      isPlaylistVideo,
      showFirstResponseOverlay,
      showLoginPrompt,
    ]);

    const PlaylistScaleOverlay = useMemo(() => {
      if (
        showFirstResponseOverlay &&
        !isLoggedIn &&
        !config.allowUnauthenticatedVideoPlayback
      ) {
        return null;
      }

      if (!showFirstResponseOverlay) return null;

      return (
        <Stack
          w="100%"
          h="100%"
          pos="absolute"
          top={0}
          bottom={0}
          right={0}
          gap={0}
          style={{ zIndex: 1 }}
          justify={isLaptopOrBigger ? "center" : "flex-start"}
          align={isLaptopOrBigger ? "center" : "center"}
          bg="rgba(255, 255, 255, 0.85)"
          px="xl"
          pt="md"
        >
          <Title order={isLaptopOrBigger ? 4 : 5}>
            {understandingBeforeStepConfig?.title}
          </Title>
          <Space h="md" />
          <PlaylistScale
            id={`${activePlaylist?.id}-response-before`}
            isSmall={!isLaptopOrBigger}
            playlistStepKey={`understanding-response-before`}
            playlistId={activePlaylist?.id}
            lowerText={understandingBeforeStepConfig?.stepParams?.lowerText}
            upperText={understandingBeforeStepConfig?.stepParams?.upperText}
            isVideoOverlay={true}
            onSelect={() => {
              Navigation.navToPlaylistStep("video-1");
              // play the video
              // handlePlayVideo();
            }}
          />
          <Space h="xl" />
          <Group justify="center" align="center" gap={4}>
            <IconInfoCircle size={20} color={theme.colors.primary[7]} />
            <Text
              ta="left"
              c="text.6"
              flex={1}
              lh={1}
              fz={isLaptopOrBigger ? 12 : 10}
            >
              Your response helps us recommend the best videos for you.
            </Text>
          </Group>
        </Stack>
      );
    }, [
      activePlaylist?.id,
      isLaptopOrBigger,
      isLoggedIn,
      showFirstResponseOverlay,
      theme.colors.primary,
      understandingBeforeStepConfig?.stepParams?.lowerText,
      understandingBeforeStepConfig?.stepParams?.upperText,
      understandingBeforeStepConfig?.title,
    ]);

    const handleVideoReaction = useCallback(
      (reaction: "positive" | "neutral" | "negative") => {
        if (!video) return false;

        // Update user profile
        UserProfileService.updateVideoReaction({
          vimeoId: video.vimeoId,
          reaction,
          questionNumber: questionNumber || 0,
          advizerId: advizerId || "",
        });

        // Get current video state from player
        let currentVideoWatchedSeconds: number | undefined =
          Number.isFinite(secondsWatched) && secondsWatched >= 0
            ? Math.floor(secondsWatched)
            : undefined;
        const currentVideoDurationSeconds: number | undefined =
          Number.isFinite(durationInSeconds) && durationInSeconds > 0
            ? Math.floor(durationInSeconds)
            : undefined;
        let currentVideoWatchedPercent: number | undefined =
          Number.isFinite(lastReportedPercent.current) &&
          lastReportedPercent.current >= 0
            ? Math.floor(lastReportedPercent.current)
            : undefined;

        try {
          const player = reactPlayerRef.current?.getInternalPlayer();
          const currentTime = player?.getCurrentTime?.();
          if (
            typeof currentTime === "number" &&
            Number.isFinite(currentTime) &&
            currentTime >= 0
          ) {
            currentVideoWatchedSeconds = Math.floor(currentTime);
            if (
              typeof durationInSeconds === "number" &&
              Number.isFinite(durationInSeconds) &&
              durationInSeconds > 0
            ) {
              currentVideoWatchedPercent = Math.floor(
                (currentTime / durationInSeconds) * 100,
              );
              // Ensure percent is between 0 and 100
              currentVideoWatchedPercent = Math.max(
                0,
                Math.min(100, currentVideoWatchedPercent),
              );
            }
          }
        } catch (e) {
          console.error("Error getting current video state", e);
        }

        // Prepare analytics data, only including valid numbers
        const analyticsData: {
          videoWatchedSeconds?: number;
          videoDurationSeconds?: number;
          videoWatchedPercent?: number;
          userSecondsWatched?: number;
          videoHasSeeked?: boolean;
          isPlaylistVideo?: boolean;
          isPlaylistVideoString?: string;
          isAssignmentVideo?: boolean;
          context?: string;
        } = {
          videoWatchedSeconds: currentVideoWatchedSeconds,
          videoDurationSeconds: currentVideoDurationSeconds,
          videoWatchedPercent: currentVideoWatchedPercent,
          videoHasSeeked: hasSeeked,
          isPlaylistVideo,
          isPlaylistVideoString: isPlaylistVideo ? "true" : "false",
          isAssignmentVideo,
          context: isPlaylistVideo ? "playlist" : undefined,
        };

        // Only include userSecondsWatched if it's a valid number
        if (
          typeof userSecondsWatched === "number" &&
          Number.isFinite(userSecondsWatched) &&
          userSecondsWatched >= 0
        ) {
          analyticsData.userSecondsWatched = Math.floor(userSecondsWatched);
        }

        // Track analytics event with comprehensive video data
        Analytics.videoReaction({
          video,
          reaction,
          ...analyticsData,
          isRecommendedAdvizer,
        });

        return true;
      },
      [
        video,
        questionNumber,
        advizerId,
        secondsWatched,
        durationInSeconds,
        userSecondsWatched,
        hasSeeked,
        isPlaylistVideo,
        isAssignmentVideo,
        isRecommendedAdvizer,
      ],
    );

    const VideoFeedback = useMemo(() => {
      if (showFirstResponseOverlay || isInBackground || isPartnerApp)
        return null;

      return (
        <Stack
          w="100%"
          // h={80}
          py="xs"
          justify="center"
          align="center"
          bg="primary.9"
          // pos="absolute"
          // top={0}
          // right={0}
          style={{
            zIndex: 1,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
          }}
        >
          <VideoFeedbackButtons
            vimeoId={video?.vimeoId || ""}
            onReaction={handleVideoReaction}
          />
        </Stack>
      );
    }, [
      showFirstResponseOverlay,
      isInBackground,
      isPartnerApp,
      video?.vimeoId,
      handleVideoReaction,
    ]);

    const renderCoreContent = useCallback(() => {
      if (!videoId) {
        return null;
      }

      if (videoError) {
        return (
          <Image
            alt="Travolta confused meme"
            src={travoltaGif}
            fit="scale-down"
            noRatio
            quote={quote}
            memeText={
              <>
                <Text
                  size="xl"
                  titleStyle="h2"
                  c="white"
                  style={{ WebkitTextStroke: "1px black" }}
                >
                  So... where's the video?
                </Text>
                <Text
                  titleStyle="h3"
                  ta="center"
                  c="white"
                  style={{ WebkitTextStroke: "1px black" }}
                  maw={400}
                >
                  Our bad! Seems something went wrong and we'll look into it
                  ASAP.
                </Text>
              </>
            }
          />
        );
      }

      // iOS + light mode special-case
      if (isIOS && light && !forceNoLight) {
        if (!iosMountedFullPlayer) {
          // Step 1: show the thumbnail (light). First tap only mounts the real player.
          return (
            <>
              <ReactPlayer
                ref={playerRef}
                url={`https://vimeo.com/${video?.vimeoId}`}
                light={vimeoThumbnailUrl}
                controls
                playsinline
                playing={false} // do NOT try to autoplay here
                playIcon={
                  <Group
                    className={isPlayHovered ? "pulseBorder" : ""}
                    bg={
                      isPlayHovered
                        ? alpha(theme.colors.primary[9], 0.9)
                        : alpha(theme.colors.primary[9], 0.1)
                    }
                    p={8}
                    style={{
                      borderRadius: "50%",
                      cursor: "pointer",
                      zIndex: 1,
                    }}
                  >
                    <IconPlayerPlayFilled
                      color={
                        isPlayHovered
                          ? theme.colors.text[0]
                          : theme.colors.text[2]
                      }
                      size={40}
                    />
                  </Group>
                }
                onClickPreview={() => {
                  handlePlayVideo(); // your analytics / active video id
                  setIosMountedFullPlayer(true);
                  setIosAttemptAutoplay(true); // try one-tap autoplay after mount
                }}
                onReady={handleVideoReady}
                onError={(e) => {
                  console.error("Vimeo (light) error", e);
                  setDidPlayerUrlFail(true);
                }}
                width="100%"
                height="100%"
                config={vimeoCfg}
              />
              {FloatingDescriptors}
              {LoginOverlay}
              {PlaylistScaleOverlay}
            </>
          );
        }

        // Step 2: mount the real (non-light) player. Try to start; fall back to controls if iOS blocks it.
        return (
          <>
            <ReactPlayer
              ref={playerRef}
              url={`https://vimeo.com/${video?.vimeoId}`}
              light={false}
              controls
              playsinline
              playing={iosAttemptAutoplay} // best-effort one-tap
              onReady={() => {
                handleVideoReady();
                // Nudge autoplay in the same task; if it fails, we’ll disable playing below.
                queueMicrotask(() => {
                  requestAnimationFrame(() => {
                    const p = playerRef.current?.getInternalPlayer();
                    p?.play?.(); // some iOS versions accept this; others ignore
                    // Optional: attempt to unmute after a genuine play event later
                  });
                });
                // Safety: if not actually playing within ~600ms, stop trying—show controls cleanly
                setTimeout(() => setIosAttemptAutoplay(false), 600);
              }}
              onError={(e) => {
                console.error("Vimeo (mounted) error", e);
                setDidPlayerUrlFail(true);
                setIosAttemptAutoplay(false);
              }}
              width="100%"
              height="100%"
              config={vimeoCfg}
            />
            {/* Important: overlays should not block taps once iframe is up */}
            <>
              {FloatingDescriptors}
              {LoginOverlay}
              {PlaylistScaleOverlay}
            </>
          </>
        );
      }

      // console.log("RENDERING VIDEO", {
      //   videoId,
      //   questionNumber,
      //   url,
      //   preppedUrl,
      //   title,
      //   videoError,
      // });

      return (
        <>
          <ReactPlayer
            ref={reactPlayerRef}
            config={{
              vimeo: {
                playerOptions: {
                  cc: true,
                  ...(isMobileDevice
                    ? {
                        muted: 1, // allows autoplay after your preview tap
                        playsinline: 1, // avoid forced fullscreen on iOS
                        autopause: 0, // don't randomly pause when multiple iframes exist
                        dnt: 1,
                      }
                    : {}),
                },
              },
            }}
            url={preppedUrl || url}
            width="100%"
            height="100%"
            playing={isVideoPlaying || playNow}
            light={
              light && !forceNoLight && !isMobileDevice
                ? vimeoThumbnailUrl
                : false
            }
            controls
            playsinline={true}
            volume={1}
            playbackRate={playbackRate}
            fallback={<Box h="100%" w="100%" bg="background.4" />}
            style={{
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            playIcon={
              <Group
                className={isPlayHovered ? "pulseBorder" : ""}
                bg={
                  isPlayHovered
                    ? alpha(theme.colors.primary[9], 0.9)
                    : alpha(theme.colors.primary[9], 0.1)
                }
                p={8}
                style={{ borderRadius: "50%", cursor: "pointer", zIndex: 1 }}
              >
                <IconPlayerPlayFilled
                  color={
                    isPlayHovered ? theme.colors.text[0] : theme.colors.text[2]
                  }
                  size={40}
                />
              </Group>
            }
            onReady={handleVideoReady}
            onStart={() => {
              if (START_VIDEO_FOR_PROMPT && !explicitShowPrompt) {
                setExplicitShowPrompt(true);
                reactPlayerRef.current?.getInternalPlayer()?.pause();

                return;
              }
              resetVideo();
              incrementNumVideosPlayed();
              Analytics.startVideo({
                video,
                videoDurationSeconds: durationInSeconds,
                isPlaylistVideoString: isPlaylistVideo ? "true" : "false",
                isAssignmentVideo,
                isRecommendedAdvizer,
              });
            }}
            onProgress={handleVideoProgress}
            onDuration={(d) => {
              setDurationInSeconds(d);
            }}
            onPlay={() => {
              if (explicitShowPrompt) {
                // pause video
                reactPlayerRef.current?.getInternalPlayer()?.pause();
                return;
              }
              setActiveVideoId(videoId);
              handlePlayVideo();
            }}
            // onError={(error) => {
            //   console.error("VIDEO FAILED TO LOAD:", error);
            //   console.error("Failed video details:", { videoId, url, title });
            // }}
            onPause={() => {
              Analytics.pauseVideo({
                video,
                videoWatchedSeconds: secondsWatched,
                videoWatchedPercent: lastReportedPercent.current,
                videoDurationSeconds: durationInSeconds,
                userSecondsWatched,
                isAssignmentVideo,
                isRecommendedAdvizer,
              });
            }}
            onClickPreview={() => {
              if (isMobileDevice) {
                setPlayNow(true);
              }

              handlePlayVideo();
            }}
            onPlaybackRateChange={(rate: number) => {
              setPlaybackRate(rate);
            }}
            onSeek={(seconds: number) => {
              if (
                !showAssignmentHeader ||
                seconds < 2 ||
                !config.isProductionClient ||
                // @ts-ignore
                window.enableVideoSeeking
              )
                return;
              if (
                lastVideoTimeForSeeking &&
                seconds > lastVideoTimeForSeeking &&
                seconds > secondsWatchedOfVideo
              ) {
                // Notifications.showNotification({
                //   title: "Seeking is not an option for assignments.",
                //   type: "warning",
                // });
                reactPlayerRef.current
                  ?.getInternalPlayer()
                  ?.setCurrentTime(
                    Math.max(lastVideoTimeForSeeking, secondsWatchedOfVideo),
                  );
              }
            }}
          />

          {FloatingDescriptors}
          {LoginOverlay}
          {PlaylistScaleOverlay}
          {/* {FloatingVideoWatchedStatus} */}
        </>
      );
    }, [
      FloatingDescriptors,
      LoginOverlay,
      PlaylistScaleOverlay,
      durationInSeconds,
      explicitShowPrompt,
      forceNoLight,
      handlePlayVideo,
      handleVideoProgress,
      handleVideoReady,
      incrementNumVideosPlayed,
      iosAttemptAutoplay,
      iosMountedFullPlayer,
      isAssignmentVideo,
      isIOS,
      isMobileDevice,
      isPlayHovered,
      isPlaylistVideo,
      isVideoPlaying,
      light,
      playNow,
      playbackRate,
      preppedUrl,
      quote,
      resetVideo,
      secondsWatched,
      secondsWatchedOfVideo,
      setActiveVideoId,
      showAssignmentHeader,
      theme.colors.primary,
      theme.colors.text,
      url,
      userSecondsWatched,
      video,
      videoError,
      videoId,
      vimeoCfg,
      isRecommendedAdvizer,
    ]);

    useEffect(() => {
      if (isInBackground && isVideoPlaying) {
        setActiveVideoId(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInBackground]);

    useEffect(() => {
      if (!light) {
        setIsVideoReady(false);
      }
    }, [videoId, light]);

    useEffect(() => {
      if (url && !isInBackground) {
        const shareCopyEmbedUrl = `https://noembed.com/embed?url=${shareCopyUrl}`;
        const playerEmbedUrl = `https://noembed.com/embed?url=${playerUrl}`;

        fetch(playerEmbedUrl)
          .then((res) => res.json())
          .then((data) => {
            if (data?.error) {
              setDidPlayerUrlFail(true);

              Analytics.videoError({
                error: data.error,
                videoId,
                url: playerUrl,
                isAssignmentVideo,
                isRecommendedAdvizer,
              });

              fetch(shareCopyEmbedUrl)
                .then((res) => res.json())
                .then((data) => {
                  if (data?.error) {
                    setDidShareUrlFail(true);
                    Analytics.videoError({
                      error: data.error,
                      videoId,
                      url: shareCopyUrl,
                      isAssignmentVideo,
                      isRecommendedAdvizer,
                    });
                  } else {
                    setDidShareUrlFail(false);
                  }
                });
            } else {
              setDidPlayerUrlFail(false);
            }
          });
      }
    }, [
      url,
      isInBackground,
      videoId,
      isAssignmentVideo,
      assignmentId,
      shareCopyUrl,
      playerUrl,
      isRecommendedAdvizer,
    ]);

    useEffect(() => {
      if (playOnMount) {
        handlePlayVideo();
      }
    }, [playOnMount, handlePlayVideo]);

    // useEffect(() => {
    //   if (autoplay) {
    //     setForceNoLight(true);

    //     setTimeout(() => {
    //       if (reactPlayerRef.current) {
    //         handlePlayVideo();
    //         setForceNoLight(false);

    //         // reactPlayerRef.current.getInternalPlayer()?.play?.();
    //       }
    //     }, 100);
    //   }
    // }, [autoplay, handlePlayVideo, isVideoPlaying]);

    const CopyVideoLink = useMemo(() => {
      return (
        <CopyLink
          propsLink={`${window.location.origin}/advizers/${advizerId}?question=${questionNumber}`}
          text="Copy share link"
          context="Advizer Video - Copy advizer link"
          buttonProps={{
            variant: "filled",
          }}
        />
      );
    }, [advizerId, questionNumber]);

    const CopyVimeoUrlLink = useMemo(() => {
      if (!video?.vimeoId) return null;

      return (
        <CopyLink
          propsLink={`https://vimeo.com/manage/videos/${video.vimeoId}`}
          text="Copy Vimeo URL"
          context="Advizer Video - Copy Vimeo URL"
          buttonProps={{
            variant: "filled",
          }}
        />
      );
    }, [video?.vimeoId]);

    return (
      <Stack
        gap={0}
        h="100%"
        w="100%"
        justify="flex-start"
        style={{ borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }}
        display={isInBackground ? "none" : "flex"}
      >
        {title && (
          <Group
            bg="primary.9"
            px={withControls ? "xs" : "md"}
            py="xs"
            w="100%"
            justify="space-between"
            gap={8}
          >
            {onClickPrevious && withControls ? (
              <NextPrevButton onClick={onClickPrevious} hide={!withControls} />
            ) : null}

            <Group p={0} justify="flex-start" flex={1} w="100%">
              <Text
                titleStyle="h1"
                fz={videoTitleFontSize}
                flex={1}
                c={theme.colors.text[0]}
                ta="left"
                px={withControls ? 4 : 0}
                lineClamp={videoTitleLineClamp}
              >
                {questionNumber && (
                  <span
                    style={{ color: theme.colors.primary[3], paddingRight: 8 }}
                  >
                    Q{questionNumber === 13.1 ? "13B" : String(questionNumber)}
                  </span>
                )}
                {title}
              </Text>
              {config.isPartnerApp && withControls && advizerId
                ? CopyVideoLink
                : null}
              {SHOW_ADMIN_COPY_VIMEO_URL ? CopyVimeoUrlLink : null}
            </Group>

            {onClickNext && withControls ? (
              <NextPrevButton
                onClick={onClickNext}
                isRight
                hide={!withControls}
              />
            ) : null}

            {!withControls && advizerId && config.isPartnerApp
              ? CopyVideoLink
              : null}
          </Group>
        )}
        <AspectRatio
          component="div"
          ratio={16 / 9}
          w="100%"
          pos="relative"
          style={{
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            overflow: "hidden",
            cursor: "pointer",
          }}
          bg="background.2"
          onMouseEnter={() => {
            setIsPlayHovered(true);
          }}
          onMouseLeave={() => {
            setIsPlayHovered(false);
          }}
        >
          {renderCoreContent()}
        </AspectRatio>
        {VideoFeedback}
      </Stack>
    );
  },
);

function NextPrevButton(props: {
  onClick: () => void;
  isRight?: boolean;
  hide?: boolean;
}) {
  const { theme } = useStyles();
  return (
    <Stack h={42}>
      <ActionIcon
        onClick={props.hide ? undefined : props.onClick}
        size={props.hide ? 10 : "xl"}
        radius="xl"
        color={lighten(theme.colors.primary[9], 0.075)}
        opacity={props.hide ? 0 : 1}
        ariaLabel={props.isRight ? "Go to previous video" : "Go to next video"}
      >
        {props.isRight ? <IconArrowRight /> : <IconArrowLeft />}
      </ActionIcon>
    </Stack>
  );
}
